import { createReducer, on } from '@ngrx/store';
import { ApprovalRequestDetail } from '@shared/models/approval.model';
import * as ApprovalActions from './approval.actions';
import {
  ApprovalRequestItem,
  ApprovalFilters,
  ApprovalMode,
  RequesterInfo,
} from '../models/approval.model';
import { DEFAULT_PAGE_SIZE } from '../constants/approval.constants';

/**
 * NgRx feature key for the approval slice. Centralised here so the
 * `app.config.ts` registration and the selectors stay in lock-step.
 */
export const APPROVAL_FEATURE_KEY = 'approval';

/**
 * Approval State Interface
 */
export interface ApprovalState {
  // List data
  approvalRequests: ApprovalRequestItem[];
  loading: boolean;
  error: string | null;

  // Active mode/tab
  activeMode: ApprovalMode;

  // Pagination
  pageIndex: number;
  pageSize: number;
  total: number;

  // Sort
  sortActive: string;
  sortDirection: 'ASC' | 'DESC';

  // Filters per mode
  filtersByMode: Record<ApprovalMode, ApprovalFilters>;

  // Search
  searchTerm: string;

  // Filter modal
  filterModalOpen: boolean;

  // Requesters for filter dropdown
  requesters: RequesterInfo[];
  requestersLoading: boolean;
  requestersError: string | null;

  /**
   * Detail page (used by `my-requests` and any other audience that
   * renders a single approval request through the slice).
   */
  selectedRequest: ApprovalRequestDetail | null;
  detailLoading: boolean;
  detailError: string | null;

  /**
   * Cancel mutation state for the requester's "withdraw my pending
   * request" CTA. Approve / reject for the approver audiences go
   * through the verification slice and do not land here.
   */
  cancelLoading: boolean;
  cancelError: string | null;
}

/**
 * Initial State
 */
export const initialState: ApprovalState = {
  // List data
  approvalRequests: [],
  loading: false,
  error: null,

  // Active mode/tab
  activeMode: 'mine',

  // Pagination
  pageIndex: 0,
  pageSize: DEFAULT_PAGE_SIZE,
  total: 0,

  // Sort
  sortActive: 'createdAt',
  sortDirection: 'DESC',

  // Filters per mode
  filtersByMode: {
    all: {},
    mine: {},
    myRequests: {},
  },

  // Search
  searchTerm: '',

  // Filter modal
  filterModalOpen: false,

  // Requesters
  requesters: [],
  requestersLoading: false,
  requestersError: null,

  // Detail (my-requests audience)
  selectedRequest: null,
  detailLoading: false,
  detailError: null,

  // Cancel (my-requests audience)
  cancelLoading: false,
  cancelError: null,
};

/**
 * Approval Reducer
 */
export const approvalReducer = createReducer(
  initialState,

  // Load Approval Requests
  on(ApprovalActions.loadApprovalRequests, state => ({
    ...state,
    loading: true,
    error: null,
  })),

  on(
    ApprovalActions.loadApprovalRequestsSuccess,
    (state, { data, pagination }) => ({
      ...state,
      approvalRequests: data,
      total: pagination.total,
      loading: false,
      error: null,
    })
  ),

  on(ApprovalActions.loadApprovalRequestsFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),

  // Tab Change
  on(ApprovalActions.setActiveTab, (state, { mode }) => ({
    ...state,
    activeMode: mode,
    pageIndex: 0, // Reset to first page when changing tabs
  })),

  // Pagination
  on(ApprovalActions.setPage, (state, { pageIndex }) => ({
    ...state,
    pageIndex,
  })),

  on(ApprovalActions.setPageSize, (state, { pageSize }) => ({
    ...state,
    pageSize,
    pageIndex: 0, // Reset to first page when changing page size
  })),

  // Sort
  on(ApprovalActions.setSort, (state, { sort }) => ({
    ...state,
    sortActive: sort.active,
    sortDirection: sort.direction,
  })),

  // Filters
  on(ApprovalActions.setFilters, (state, { filters }) => ({
    ...state,
    filtersByMode: {
      ...state.filtersByMode,
      [state.activeMode]: filters,
    },
    pageIndex: 0, // Reset to first page when applying filters
  })),

  on(ApprovalActions.clearFilters, state => ({
    ...state,
    filtersByMode: {
      ...state.filtersByMode,
      [state.activeMode]: {},
    },
    pageIndex: 0,
  })),

  on(ApprovalActions.removeFilterChip, (state, { key, value }) => {
    const currentFilters = { ...state.filtersByMode[state.activeMode] };

    switch (key) {
      case 'dateRange':
        currentFilters.createdFrom = null;
        currentFilters.createdTo = null;
        break;
      case 'requester':
        currentFilters.requesterIds = (
          currentFilters.requesterIds ?? []
        ).filter(id => id !== value);
        if (currentFilters.requesterIds.length === 0) {
          currentFilters.requesterIds = null;
        }
        break;
      case 'domain':
        currentFilters.domain = null;
        break;
      case 'status':
        currentFilters.status = null;
        break;
      // The action chip is only ever attached on the `'myRequests'`
      // audience; on the other audiences the filter never sets `action`
      // so this branch is harmlessly a no-op for them.
      case 'action':
        currentFilters.action = null;
        break;
    }

    return {
      ...state,
      filtersByMode: {
        ...state.filtersByMode,
        [state.activeMode]: currentFilters,
      },
      pageIndex: 0,
    };
  }),

  // Search
  on(ApprovalActions.setSearch, (state, { searchTerm }) => ({
    ...state,
    searchTerm,
    pageIndex: 0,
  })),

  // Requesters
  on(ApprovalActions.loadRequesters, state => ({
    ...state,
    requestersLoading: true,
    requestersError: null,
  })),

  on(ApprovalActions.loadRequestersSuccess, (state, { requesters }) => ({
    ...state,
    requesters,
    requestersLoading: false,
    requestersError: null,
  })),

  on(ApprovalActions.loadRequestersFailure, (state, { error }) => ({
    ...state,
    requestersLoading: false,
    requestersError: error,
  })),

  // Filter Modal
  on(ApprovalActions.openFilterModal, state => ({
    ...state,
    filterModalOpen: true,
  })),

  on(ApprovalActions.closeFilterModal, state => ({
    ...state,
    filterModalOpen: false,
  })),

  // ---- Detail (my-requests audience) ------------------------------------
  on(ApprovalActions.loadApprovalRequestDetail, state => ({
    ...state,
    detailLoading: true,
    detailError: null,
    selectedRequest: null,
  })),

  on(ApprovalActions.loadApprovalRequestDetailSuccess, (state, { detail }) => ({
    ...state,
    selectedRequest: detail,
    detailLoading: false,
    detailError: null,
  })),

  on(ApprovalActions.loadApprovalRequestDetailFailure, (state, { error }) => ({
    ...state,
    detailLoading: false,
    detailError: error,
  })),

  on(ApprovalActions.clearApprovalRequestDetail, state => ({
    ...state,
    selectedRequest: null,
    detailLoading: false,
    detailError: null,
    cancelLoading: false,
    cancelError: null,
  })),

  // ---- Cancel (my-requests audience) ------------------------------------
  on(ApprovalActions.cancelApprovalRequest, state => ({
    ...state,
    cancelLoading: true,
    cancelError: null,
  })),

  on(ApprovalActions.cancelApprovalRequestSuccess, state => ({
    ...state,
    cancelLoading: false,
    cancelError: null,
  })),

  on(ApprovalActions.cancelApprovalRequestFailure, (state, { error }) => ({
    ...state,
    cancelLoading: false,
    cancelError: error,
  })),

  // Reset State
  on(ApprovalActions.resetState, () => initialState)
);
